//=====================================================
//　G003カレンダー週表示
//=====================================================
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import { getApiData } from "../../utils/CallApi";
import { CtxG003 } from "./G003"
import { G003CalendarWeeklyBody } from "./G003CalendarWeeklyBody";
import { G003CalendarWeeklyHeader } from "./G003CalendarWeeklyHeader";
import { CtxClinicInfo, CtxNewReservation, CtxSystemErr } from "../../utils/Contexts";
import { ConstMsg } from "../../utils/ConstMsg";
import { MsgBoxErr } from "../../common/MsgBoxErr";
import { LoadingBox } from "../../common/LoadingBox";
import { IFUseNewReservation } from "../../utils/UseNewReservation";
import { IFUseAuth, useAuth } from "../../utils/UseAuth";
import { ReservationType } from "../../utils/ConstCommon"

export function G003CalendarWeekly(){
    const [errmess, setErrmess] = useState("");

    const ua = useAuth();
    const cserr = useContext(CtxSystemErr);
    const ctx = useContext(CtxG003);
    const cci = useContext(CtxClinicInfo);         //医院情報
    const cnr = useContext(CtxNewReservation);     //予約情報

    const [apiData, setApiData] = useState<any>();                  //APIデータ格納
    const [tblHeaderWidth, setTblHeaderWidth] = useState(904);      //テーブル幅設定
    const [tblHeaderHeight, setTblHeaderHeight] = useState(0);      //ヘッダー高さ設定
    const [hoverCellIdx, setHoverCellIdx] = useState(-1);           //hover設定
    const [tblHeaderFixed, setTblHeaderFixed] = useState(false);    //テーブルヘッダー固定設定
    const [loadingDone, setLoadingDone] = useState(false);      	//読込中フラグ

    const refDivCalendar = useRef<HTMLDivElement>(null);        //カレンダー親divの位置を取得するためのDOM操作用変数
    const refTblHeader = useRef<HTMLDivElement>(null);          //カレンダーheaderの位置を取得するためのDOM操作用変数
    const refTblBody = useRef<HTMLDivElement>(null);            //カレンダーbodyの幅を取得するためのDOM操作用変数

    //画面リサイズ時の処理
    function resizeWindow(){
        //浮遊ヘッダーの幅を決めるためにbodyの横幅を取得し設定
        if (refTblBody.current){
            setTblHeaderWidth(refTblBody.current.offsetWidth);
        }
        //calendar-tableのpadding-top設定用にヘッダーから高さを取得する
        if (refTblHeader.current){
            setTblHeaderHeight(refTblHeader.current.offsetHeight - 1);
        }
        //スクロール位置がカレンダー親の位置より上になったら固定解除
        if (refDivCalendar.current){
            if (refDivCalendar.current.getBoundingClientRect().top > 0){
                setTblHeaderFixed(false);
            }
        }
    }

    //レンダー後処理：APIデータ取得
    useLayoutEffect(function(){
        // マウントフラグ
        let isMounted = true;
        let targetDate = ctx.moveflg || !cnr.data.date ? ctx.cDate : cnr.data.date ;
        setLoadingDone(false);
        if(cnr.data.medicalTypeId){        
            getApiDataCalendarWeekly(targetDate, cci.data.id, cnr, ua, function(res:any) {
                // アンマウント時はstateを更新しないようにする
                if (isMounted) {
                    ctx.setCDate(targetDate);
                    ctx.setCMonth(targetDate);
                    ctx.setMoveflg(false);
                    if (res?.status) {
                        if (res.status === 200) {
                            setErrmess("");
                            setApiData(res.data);
                        } else if (res.status === 400) {
                            setErrmess(res.data.message);
                        } else if (res.status === 403 || res.status === 404 || res.status >= 500) {
                            cserr.setErrmess(res.data.message);
                        } else {
                            cserr.setErrmess(ConstMsg.EG0030001);
                        }
                    } else {
                        cserr.setErrmess(ConstMsg.EG0030001);
                    }
                    setLoadingDone(true);
                }
            });
        }
        // cleanup関数(Unmount時の処理)
        return() => {
            // マウントオフ
            isMounted = false;
        }
        // 無限ループを抑止するためuaは第2引数から除外する
        // 移動フラグ(moveflg)を更新するためctxは第2引数から除外する
        // エラーメッセージを更新するため、cserrは第2引数から除外する
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx.cDate,cci.data.id,cnr]);

    //レンダー後処理：リサイズイベント処理追加
    useEffect(function(){
        window.addEventListener("resize", function(){
            resizeWindow();
        });
    }, []);

    //レンダー後処理：スクロールイベント処理追加
    useEffect(function(){
        window.addEventListener("scroll", function(){
            if (refTblHeader.current && refDivCalendar.current){
                if (tblHeaderFixed){
                    //固定中の場合、スクロール位置がカレンダー親の位置より上になったら固定解除
                    if (refDivCalendar.current.getBoundingClientRect().top > 0) {
                        setTblHeaderFixed(false);
                    }
                } else {
                    //非固定の場合、スクロール位置がヘッダーの位置より下になったら固定
                    if (refTblHeader.current.getBoundingClientRect().top <= 0){
                        setTblHeaderFixed(true);
                    }
                }
            }
        });
    }, [tblHeaderFixed]);

    return (
        <>
            {errmess && <MsgBoxErr text={errmess} />}
            <div className="calendar-table" ref={refDivCalendar} style={{paddingTop: tblHeaderHeight}}>
                <G003CalendarWeeklyHeader 
                    apiData={apiData} refElm={refTblHeader} tblHeaderWidth={tblHeaderWidth} 
                    hoverCellIdx={hoverCellIdx} tblHeaderFixed={tblHeaderFixed}  loadingDone={loadingDone}/>
                <LoadingBox overlay={false} loadingDone={loadingDone}>
                    <G003CalendarWeeklyBody 
                        apiData={apiData} refElm={refTblBody} hoverCellIdx={hoverCellIdx} 
                        setHoverCellIdx={setHoverCellIdx} resizeWindow={resizeWindow}/>
                </LoadingBox>
            </div>
        </>
    )

}

//-----------------------------------------------------
//　APIデータを取得
//-----------------------------------------------------
function getApiDataCalendarWeekly(cDate:Date, clinicId:string, reservation:IFUseNewReservation, ua:IFUseAuth, callback:any):any {
    const year = cDate.getFullYear();
    const month = cDate.getMonth() + 1;
    const date = cDate.getDate();
    const reserveId = reservation.data.reserveId;
    const patientId = (reservation.type === ReservationType.First ? "" : ua.getPatientNo());
    
    let apiUrl = `/c/${clinicId}/tables/${reservation.data.medicalTypeId}/${year}/${month}/${date}`;
    let apiParams = {
        params: {
            reserveId: reserveId,
            patientId: patientId
        }
    };

    getApiData(
        apiUrl,
        apiParams,
        function(res:AxiosResponse<any>){
            callback(res);
        }
    );
}